/**
 * ResultsLayout Component
 * Organizes all result components: summary, sections, and tips
 */

import React from 'react';
import { SummaryBox } from './SummaryBox';
import { SectionHeader } from './SectionHeader';
import { TipBox, TipBoxType } from './TipBox';
import { ResultCard } from './ResultCard';
import { Tag } from './TagChips';
import { Action } from './ActionButtons';
import { StaggeredList } from '../animations';
import { getSetting } from '../../utils/settings';

export interface ResultItem {
  title: string;
  description: string;
  icon?: string;
  trustScore?: number;
  tags?: Tag[];
  url?: string;
  actions?: Action[];
}

export interface Section {
  title: string;
  icon?: string;
  items: ResultItem[];
  showViewAll?: boolean;
  onViewAll?: () => void;
}

export interface Tip {
  text: string;
  type: TipBoxType;
}

export interface ResultsLayoutProps {
  summary?: string;
  summaryIcon?: string;
  sections: Section[];
  tips?: Tip[];
  layout?: 'grid' | 'list';
  gridColumns?: 1 | 2 | 3;
  showSections?: boolean;
  titleMaxLength?: number;
  descriptionMaxLength?: number;
  entryAnimations?: boolean;
  hoverEffects?: boolean;
}

export const ResultsLayout: React.FC<ResultsLayoutProps> = ({
  summary,
  summaryIcon,
  sections,
  tips,
  layout = 'grid',
  gridColumns = 1,
  showSections = false,
  titleMaxLength = 60,
  descriptionMaxLength = 150,
  entryAnimations = true,
  hoverEffects = true,
}) => {
  // Get settings from WordPress
  const showSectionHeaders = getSetting('results.showSectionHeaders', false);
  const resultsPerSection = getSetting('results.resultsPerSection', 5);
  const maxSections = getSetting('results.maxSections', 0);
  const minResults = getSetting('results.minResults', 10);
  
  // Get card design settings
  const cardShowBorder = getSetting('results.cardShowBorder', true);
  const cardBorderRadius = getSetting('results.cardBorderRadius', 12);
  const cardShowShadow = getSetting('results.cardShowShadow', true);

  // Filter out sections with no valid items
  const validSections = sections
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Filter out items with empty or "---" descriptions
        const desc = item.description?.trim() || '';
        return desc && desc !== '---' && desc !== '-';
      }),
    }))
    .filter(section => section.items.length > 0); // Only keep sections with valid items

  // Apply maxSections limit if specified
  const limitedSections = maxSections > 0 
    ? validSections.slice(0, maxSections)
    : validSections;

  // Apply resultsPerSection limit to each section
  const sectionsWithLimits = limitedSections.map(section => ({
    ...section,
    items: section.items.slice(0, resultsPerSection),
  }));

  // Determine CSS class based on layout and columns
  const getLayoutClass = () => {
    if (layout === 'list') {
      return 'ai360-results-list';
    }
    if (layout === 'grid' && gridColumns === 3) {
      return 'ai360-results-grid-3col';
    }
    if (layout === 'grid' && gridColumns === 2) {
      return 'ai360-results-grid-2col';
    }
    return 'ai360-results-grid-1col';
  };

  // Determine container class for animations and hover effects
  const getContainerClass = () => {
    const classes = ['ai360-results-layout'];
    if (!entryAnimations) {
      classes.push('ai360-no-animations');
    }
    if (!hoverEffects) {
      classes.push('ai360-no-hover');
    }
    return classes.join(' ');
  };

  // If showSectionHeaders is false, flatten all items into a single array
  const allItems = !showSectionHeaders ? sectionsWithLimits.flatMap(section => section.items) : [];

  // Apply minResults when not showing section headers (flat list)
  const flatItems = !showSectionHeaders && allItems.length > 0 
    ? allItems.slice(0, Math.max(minResults, allItems.length))
    : allItems;

  return (
    <div className={getContainerClass()}>
      {/* Summary Section */}
      {summary && (
        <div className="ai360-results-layout-summary">
          <SummaryBox text={summary} icon={summaryIcon} />
        </div>
      )}

      {/* Sections with Headers (when showSectionHeaders is enabled) */}
      {showSectionHeaders && sectionsWithLimits && sectionsWithLimits.length > 0 && (
        <div className="ai360-results-layout-sections">
          {sectionsWithLimits.map((section, sectionIndex) => (
            <div key={sectionIndex} className="ai360-results-layout-section">
              <SectionHeader
                title={section.title}
                icon={section.icon}
                count={section.items?.length}
                showViewAll={section.showViewAll}
                onViewAll={section.onViewAll}
              />
              {section.items && section.items.length > 0 && (
                <div className={getLayoutClass()}>
                  <StaggeredList animation="fadeIn" staggerDelay={100}>
                    {section.items.map((item, itemIndex) => (
                      <ResultCard
                        key={itemIndex}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        trustScore={item.trustScore}
                        tags={item.tags}
                        url={item.url}
                        actions={item.actions}
                        variant="default"
                        layoutVariant={layout}
                        titleMaxLength={titleMaxLength}
                        descriptionMaxLength={descriptionMaxLength}
                        cardShowBorder={cardShowBorder}
                        cardBorderRadius={cardBorderRadius}
                        cardShowShadow={cardShowShadow}
                      />
                    ))}
                  </StaggeredList>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Flat list without section headers (when showSectionHeaders is disabled) */}
      {!showSectionHeaders && flatItems && flatItems.length > 0 && (
        <div className={getLayoutClass()}>
          <StaggeredList animation="fadeIn" staggerDelay={100}>
            {flatItems.map((item, itemIndex) => (
              <ResultCard
                key={itemIndex}
                title={item.title}
                description={item.description}
                icon={item.icon}
                trustScore={item.trustScore}
                tags={item.tags}
                url={item.url}
                actions={item.actions}
                variant="default"
                layoutVariant={layout}
                titleMaxLength={titleMaxLength}
                descriptionMaxLength={descriptionMaxLength}
                cardShowBorder={cardShowBorder}
                cardBorderRadius={cardBorderRadius}
                cardShowShadow={cardShowShadow}
              />
            ))}
          </StaggeredList>
        </div>
      )}

      {/* Tips Section */}
      {tips && tips.length > 0 && (
        <div className="ai360-results-layout-tips">
          {tips.map((tip, tipIndex) => (
            <TipBox 
              key={tipIndex} 
              text={tip.text} 
              type={tip.type} 
              delay={tipIndex * 0.1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
